// Remembers a list page's pagination and filters in the browser history
// entry, so coming Back to a list (browser back or a $router.back() arrow)
// lands on the same page with the same filters instead of page 1.
//
// Applies to routed page components (not dialogs or child components) that
// keep the usual `page` number and, optionally, a `filter` object. Opening a
// list fresh - from the sidebar or a link - makes a new history entry with
// nothing saved, so it still starts at page 1.

const STATE_KEY = "listState";

function isRoutedPage(vm) {
  // vue-router flags the vnode of the component it renders in <router-view>
  return !!(vm.$vnode && vm.$vnode.data && vm.$vnode.data.routerView);
}

function savedState(vm) {
  const state = window.history.state;
  const saved = state && state[STATE_KEY];
  return saved && saved.path === vm.$route.fullPath ? saved : null;
}

function clone(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return undefined;
  }
}

export default {
  beforeCreate() {
    if (!isRoutedPage(this) || typeof this.$options.data !== "function") return;
    const saved = savedState(this);
    if (!saved) return;

    // Restore before data is reactive, so `page` watchers don't fire and the
    // page's own first load already fetches the remembered page.
    const originalData = this.$options.data;
    this.$options.data = function () {
      const data = originalData.apply(this, arguments) || {};
      if (typeof data.page === "number" && Number(saved.page) > 0) {
        data.page = Number(saved.page);
      }
      if (typeof data.rowsPerPage === "number" && Number(saved.rowsPerPage) > 0) {
        data.rowsPerPage = Number(saved.rowsPerPage);
      }
      if (data.filter && typeof data.filter === "object" && saved.filter) {
        Object.keys(saved.filter).forEach((key) => {
          if (key in data.filter) data.filter[key] = saved.filter[key];
        });
      }
      return data;
    };
  },

  created() {
    if (!isRoutedPage(this) || typeof this.page !== "number") return;

    const save = () => {
      const state = window.history.state || {};
      try {
        window.history.replaceState(
          {
            ...state,
            [STATE_KEY]: {
              path: this.$route.fullPath,
              page: this.page,
              rowsPerPage: typeof this.rowsPerPage === "number" ? this.rowsPerPage : undefined,
              filter: this.filter && typeof this.filter === "object" ? clone(this.filter) : undefined,
            },
          },
          ""
        );
      } catch (e) {
        // history state unavailable - lists just start at page 1 as before
      }
    };

    this.$watch("page", save);
    if (typeof this.rowsPerPage === "number") this.$watch("rowsPerPage", save);
    if (this.filter && typeof this.filter === "object") this.$watch("filter", save, { deep: true });
  },
};
